import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Navbar from './Navbar';
import { PageSEO, softwareAppSchema } from '../utils/seo';

const screens = [
  { image: '/images/ipairly/screen_1.webp' },
  { image: '/images/ipairly/screen_2.webp' },
  { image: '/images/ipairly/screen_3.webp' },
  { image: '/images/ipairly/screen_4.webp' },
];

const features = [
  { icon: '🎮', title: 'Da 2 a 6 giocatori', desc: 'Per coppie, amici e gruppi. Si gioca subito, senza setup.' },
  { icon: '🌿', title: '4 livelli di intensità', desc: 'Da Chill a Estremo — il ritmo lo decidete voi.' },
  { icon: '🔒', title: 'Privacy totale', desc: 'Nessun account, nessun dato inviato. Tutto cifrato sul dispositivo.' },
  { icon: '✨', title: 'Sfide personalizzate', desc: 'Aggiungete le vostre sfide segrete al gioco.' },
  { icon: '📍', title: 'Modalità LDR', desc: 'Per le coppie a distanza — sfide in tempo reale.' },
  { icon: '🎁', title: '100% gratuito', desc: 'Nessun limite di sessioni, nessun paywall. Gratis per sempre.' },
];

const packs = [
  { icon: '💆', name: 'Aftercare', desc: 'Connessione profonda e cura reciproca.' },
  { icon: '🎭', name: 'Roleplay', desc: 'Scenari creativi e fantasia.' },
  { icon: '🎄', name: 'Festività', desc: 'San Valentino, Natale, anniversari.' },
  { icon: '🧘', name: 'Mindful', desc: 'Lentezza, gratitudine, ascolto.' },
];

const stats = [
  { value: '110+', label: 'Sfide gratuite' },
  { value: '4',    label: 'Livelli di intensità' },
  { value: '6',    label: 'Giocatori max' },
  { value: '0€',   label: 'Per iniziare' },
];

function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

export default function IPairly() {
  const [activeScreen, setActiveScreen] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const interval = setInterval(() => {
      setActiveScreen(prev => (prev + 1) % screens.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <PageSEO
        title="iPairly — Giochi per Coppie e Gruppi"
        description="iPairly è l'app di giochi e sfide per coppie e gruppi fino a 6 persone. Modalità a distanza in tempo reale, dati crittografati sul dispositivo, zero pubblicità."
        canonical="/ipairly"
        jsonLd={softwareAppSchema({
          name: 'iPairly',
          description: 'App di giochi e sfide interattive per coppie e gruppi fino a 6 persone, con modalità a distanza in tempo reale.',
          url: 'https://vmdproject.com/ipairly',
          operatingSystem: 'iOS, Android',
          applicationCategory: 'LifestyleApplication',
          image: screens[0].image,
        })}
      />
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          background: 'var(--color-bg)',
          minHeight: '100vh',
          fontFamily: 'var(--font-display)',
          color: 'var(--color-text)',
          overflowX: 'hidden',
        }}
      >

        {/* ─── Hero ─── */}
        <section style={{ paddingTop: '120px', paddingBottom: '100px' }}>
          <div className="container" style={{ display: 'flex', alignItems: 'center', gap: '72px', flexWrap: 'wrap' }}>

            {/* Left */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              <motion.div
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.82rem',
                  color: 'var(--color-accent-mid)',
                  letterSpacing: '0.05em',
                  marginBottom: '1.5rem',
                }}>
                  // ipairly
                </div>

                <h1 style={{
                  fontSize: 'clamp(2.6rem, 5vw, 4rem)',
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: '-0.03em',
                  marginBottom: '1.25rem',
                  background: 'linear-gradient(135deg, #c4b5fd 0%, #f472b6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  Il gioco che non dimenticherete.
                </h1>

                <p style={{
                  fontSize: '1.05rem',
                  color: 'var(--color-text-2)',
                  lineHeight: 1.75,
                  maxWidth: '440px',
                  marginBottom: '2.5rem',
                }}>
                  Sfide su misura per <strong style={{ color: 'var(--color-text)', fontWeight: 600 }}>coppie e gruppi</strong> fino a 6 persone.
                  Offline, privato, gratuito. La safe word ferma tutto con un tap.
                </p>

                {/* Store buttons */}
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '3rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(124,58,237,0.35)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '12px 22px',
                      textDecoration: 'none',
                      color: 'var(--color-text)',
                    }}
                  >
                    <span style={{ fontSize: '28px', lineHeight: 1 }}>🍎</span>
                    <div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Presto su</div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.02em' }}>App Store</div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(167,139,250,0.25)',
                      borderRadius: 'var(--radius-lg)',
                      padding: '12px 22px',
                      textDecoration: 'none',
                      color: 'var(--color-text)',
                    }}
                  >
                    <span style={{ fontSize: '28px', lineHeight: 1 }}>🤖</span>
                    <div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.68rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>Presto su</div>
                      <div style={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '-0.02em' }}>Google Play</div>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
                  {stats.map(({ value, label }) => (
                    <div key={label}>
                      <div style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '1.75rem',
                        fontWeight: 700,
                        color: 'var(--color-accent-mid)',
                        lineHeight: 1,
                        letterSpacing: '-0.03em',
                      }}>
                        {value}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--color-muted)', marginTop: '4px', fontFamily: 'var(--font-mono)' }}>
                        {label}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right — phone mockup */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
              style={{ flex: '1', minWidth: '240px', display: 'flex', justifyContent: 'center' }}
            >
              <div style={{ position: 'relative', width: '260px' }}>
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  left: '-50px',
                  width: '220px',
                  height: '220px',
                  background: 'radial-gradient(circle, rgba(124,58,237,0.3) 0%, transparent 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: '-30px',
                  right: '-30px',
                  width: '180px',
                  height: '180px',
                  background: 'radial-gradient(circle, rgba(244,114,182,0.2) 0%, transparent 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'relative',
                  zIndex: 1,
                  background: 'rgba(13,13,24,0.8)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '36px',
                  padding: '10px',
                  boxShadow: '0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,58,237,0.2)',
                }}>
                  <img
                    src={screens[activeScreen].image}
                    alt="iPairly screenshot"
                    style={{ width: '100%', borderRadius: '28px', display: 'block', transition: 'opacity 0.4s ease' }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '7px', marginTop: '18px', position: 'relative', zIndex: 1 }}>
                  {screens.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveScreen(i)}
                      style={{
                        width: i === activeScreen ? '22px' : '7px',
                        height: '7px',
                        borderRadius: '4px',
                        background: i === activeScreen ? 'var(--color-accent)' : 'rgba(255,255,255,0.12)',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        padding: 0,
                      }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>

          </div>
        </section>

        {/* ─── Features ─── */}
        <section className="section" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className="container">

            <Reveal>
              <div style={{ marginBottom: '4rem' }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.78rem',
                  color: 'var(--color-accent-mid)',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  marginBottom: '1rem',
                }}>
                  // perché iPairly
                </div>
                <h2 style={{
                  fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  maxWidth: '540px',
                }}>
                  Tutto quello che serve,{' '}
                  <span style={{
                    background: 'linear-gradient(135deg, #c4b5fd 0%, #f472b6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    niente di superfluo.
                  </span>
                </h2>
              </div>
            </Reveal>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
              gap: '1.25rem',
            }}>
              {features.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.07}>
                  <div
                    className="glass-card"
                    style={{
                      padding: '2rem',
                      height: '100%',
                      transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.borderColor = 'rgba(124,58,237,0.4)';
                      e.currentTarget.style.boxShadow = '0 16px 40px rgba(124,58,237,0.12)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'rgba(124,58,237,0.14)',
                      border: '1px solid rgba(124,58,237,0.25)',
                      borderRadius: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      marginBottom: '1.25rem',
                    }}>
                      {f.icon}
                    </div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      marginBottom: '0.6rem',
                      color: 'var(--color-text)',
                    }}>
                      {f.title}
                    </h3>
                    <p style={{
                      fontSize: '0.9rem',
                      color: 'var(--color-muted)',
                      lineHeight: 1.7,
                      margin: 0,
                    }}>
                      {f.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

          </div>
        </section>

        {/* ─── Packs ─── */}
        <section className="section" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className="container">

            <Reveal>
              <div style={{ marginBottom: '4rem' }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.78rem',
                  color: 'var(--color-accent-mid)',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  marginBottom: '1rem',
                }}>
                  // contenuti extra
                </div>
                <h2 style={{
                  fontSize: 'clamp(1.8rem, 3.5vw, 2.75rem)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  marginBottom: '0.75rem',
                }}>
                  Pack{' '}
                  <span style={{
                    background: 'linear-gradient(135deg, #c4b5fd 0%, #f472b6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>
                    opzionali.
                  </span>
                </h2>
                <p style={{ fontSize: '0.95rem', color: 'var(--color-muted)', fontFamily: 'var(--font-mono)' }}>
                  Acquisto unico · Nessun abbonamento · Mai necessari per divertirsi.
                </p>
              </div>
            </Reveal>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1.25rem',
            }}>
              {packs.map((p, i) => (
                <Reveal key={p.name} delay={i * 0.08}>
                  <div
                    style={{
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(20px)',
                      borderRadius: '20px',
                      padding: '2rem 1.5rem',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.borderColor = 'rgba(124,58,237,0.35)';
                      e.currentTarget.style.boxShadow = '0 16px 40px rgba(124,58,237,0.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: i % 2 === 0
                        ? 'linear-gradient(90deg, var(--color-accent), var(--color-accent-mid))'
                        : 'linear-gradient(90deg, var(--color-accent-mid), var(--color-pink))',
                    }} />
                    <div style={{ fontSize: '2.6rem', marginBottom: '1rem' }}>{p.icon}</div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      marginBottom: '0.5rem',
                      color: 'var(--color-text)',
                    }}>
                      Pack {p.name}
                    </h3>
                    <p style={{
                      fontSize: '0.88rem',
                      color: 'var(--color-muted)',
                      lineHeight: 1.6,
                      margin: '0 0 1.25rem',
                    }}>
                      {p.desc}
                    </p>
                    <span className="stack-badge" style={{ fontSize: '0.82rem' }}>
                      2,99€ · una tantum
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>

          </div>
        </section>

        {/* ─── Privacy banner ─── */}
        <section className="section" style={{ borderTop: '1px solid var(--color-border)' }}>
          <div className="container">
            <Reveal>
              <div
                className="glass-card"
                style={{
                  padding: 'clamp(2.5rem, 5vw, 4.5rem) clamp(1.75rem, 5vw, 4rem)',
                  textAlign: 'center',
                  maxWidth: '720px',
                  margin: '0 auto',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-60px',
                  right: '-60px',
                  width: '220px',
                  height: '220px',
                  background: 'radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: '-40px',
                  left: '-40px',
                  width: '180px',
                  height: '180px',
                  background: 'radial-gradient(circle, rgba(244,114,182,0.12) 0%, transparent 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }} />

                <div style={{ fontSize: '2.8rem', marginBottom: '1.25rem', position: 'relative', zIndex: 1 }}>🔒</div>

                <h2 style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.1,
                  marginBottom: '1rem',
                  position: 'relative',
                  zIndex: 1,
                }}>
                  La vostra privacy è una priorità.
                </h2>

                <p style={{
                  fontSize: '0.95rem',
                  color: 'var(--color-text-2)',
                  lineHeight: 1.8,
                  maxWidth: '480px',
                  margin: '0 auto 2rem',
                  position: 'relative',
                  zIndex: 1,
                }}>
                  Nessun dato raccolto o trasmesso. Tutto rimane cifrato sul vostro dispositivo.
                  Nessun account obbligatorio. Nessuna pubblicità.{' '}
                  <strong style={{ color: 'var(--color-accent-lt)', fontWeight: 600 }}>Mai.</strong>
                </p>

                <Link
                  to="/ipairly/policy"
                  className="btn-primary"
                  style={{ position: 'relative', zIndex: 1 }}
                >
                  Leggi la Privacy Policy →
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── Footer ─── */}
        <footer style={{ borderTop: '1px solid var(--color-border)', padding: '2rem 0' }}>
          <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '-0.02em' }}>
              VMDProject
            </span>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[
                { to: '/progetti', label: 'Tutti i progetti' },
                { to: '/ipairly/policy', label: 'Privacy' },
                { to: '/ipairly/terms', label: 'Termini' },
              ].map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  style={{ fontSize: '0.9rem', color: 'var(--color-muted)', transition: 'color 0.3s', textDecoration: 'none' }}
                  onMouseEnter={e => e.target.style.color = 'var(--color-text)'}
                  onMouseLeave={e => e.target.style.color = 'var(--color-muted)'}
                >
                  {l.label}
                </Link>
              ))}
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-muted)' }}>
              © {new Date().getFullYear()} VMDProject
            </span>
          </div>
        </footer>

      </motion.main>
    </>
  );
}
